import { useState, useEffect, useLayoutEffect, useRef, useCallback, Children } from 'react';

const getWidth = (element, { trackSize, gap }) => {
  const containerWidth = element.getBoundingClientRect().width;
  return containerWidth / trackSize - (gap - gap / trackSize);
};

const getTranslateStyle = (elementTranslateX, direction) => {
  return `translateX(${elementTranslateX[direction]}px)`;
};

const getWidthStyle = ({ trackSize, gap }) => {
  return `calc(100% / ${trackSize} - (${gap}px - ${gap}px / ${trackSize}))`;
};

export default function Slider({
  children,
  style,
  trackSize = 1,
  stepSize = trackSize,
  gap = 0,
  swipe = 0,
  interval,
  duration = '0s',
  delay,
  timing,
  step: externalStep,
  onChangeStep,
  ...props
}) {
  const container = useRef(null);
  const elements = useRef([]);
  const plugs = useRef([]);

  const timeoutID = useRef(null);
  const animationID = useRef(-1);
  const touchEvent = useRef({ start: false, move: false });
  const touchPosition = useRef({ start: 0, end: 0 });
  const elementWidth = useRef(0);
  const elementTranslateX = useRef({ prev: 0, next: 0 });

  const [init, setInit] = useState(false);
  const [innerStep, setInnerStep] = useState(0);

  const currentStep = externalStep ?? innerStep;
  const setCurrentStep = typeof externalStep === 'number' ? undefined : setInnerStep;

  let maxStep = (Children.count(children) - trackSize) / stepSize;
  maxStep = maxStep < 0 ? 0 : maxStep;

  const setElementTranslate = useCallback(() => {
    if (container.current !== null) {
      elementWidth.current = getWidth(container.current, { trackSize, gap });

      elementTranslateX.current.next =
        -(currentStep * stepSize * elementWidth.current) - currentStep * stepSize * gap;
      elementTranslateX.current.prev = elementTranslateX.current.next;
    }
  }, [currentStep, gap, stepSize, trackSize]);

  useLayoutEffect(() => {
    if (!init) {
      setElementTranslate();
      setInit(true);
    }
  }, [init, setElementTranslate]);

  /**
   * Resize
   */

  useEffect(() => {
    const handleResize = () => {
      setElementTranslate();

      elements.current.forEach((element) => {
        element.style.transform = getTranslateStyle(elementTranslateX.current, 'next');
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [trackSize, gap, setElementTranslate]);

  /**
   * Timeout
   */

  const startTimer = useCallback(() => {
    if (typeof interval === 'number' && interval >= 0 && timeoutID.current === null) {
      timeoutID.current = setTimeout(() => {
        let nextStep = currentStep + 1;
        nextStep = nextStep < 0 || nextStep > maxStep ? 0 : nextStep;

        setCurrentStep?.(nextStep);
        onChangeStep?.(nextStep);
      }, interval);
    }
  }, [interval, currentStep, maxStep, setCurrentStep, onChangeStep]);

  const stopTimer = useCallback(() => {
    if (timeoutID.current !== null) {
      clearTimeout(timeoutID.current);
      timeoutID.current = null;
    }
  }, []);

  useEffect(() => {
    startTimer();

    return () => {
      stopTimer();
    };
  }, [startTimer, stopTimer]);

  /**
   * Touch
   */

  const handleTouchStart = (event) => {
    stopTimer();

    if (swipe > 0 && !touchEvent.current.start) {
      touchEvent.current.start = true;
      touchPosition.current.start = event.touches?.[0].clientX ?? event.clientX;
      touchPosition.current.end = touchPosition.current.start;

      const animation = () => {
        if (touchEvent.current.move) {
          elements.current.forEach((element) => {
            element.style.transform = getTranslateStyle(elementTranslateX.current, 'next');
            element.style.transitionDuration = '0s';
            element.style.pointerEvents = 'none';
          });
        }

        animationID.current = requestAnimationFrame(animation);
      };

      animationID.current = requestAnimationFrame(animation);
    }
  };

  const handleTouchMove = (event) => {
    if (swipe > 0 && touchEvent.current.start) {
      plugs.current.forEach((plug) => {
        plug.style.display = 'block';
        plug.style.pointerEvents = 'auto';
      });

      touchEvent.current.move = true;
      touchPosition.current.end = event.touches?.[0].clientX ?? event.clientX;
      elementTranslateX.current.next =
        elementTranslateX.current.prev + (touchPosition.current.end - touchPosition.current.start);
    }
  };

  const handleTouchEnd = () => {
    startTimer();

    plugs.current.forEach((plug) => {
      plug.style.display = 'none';
      plug.style.pointerEvents = 'none';
    });

    if (swipe > 0 && touchEvent.current.start) {
      touchEvent.current.start = false;
      cancelAnimationFrame(animationID.current);
    }

    if (swipe > 0 && touchEvent.current.move) {
      touchEvent.current.move = false;

      elements.current.forEach((element) => {
        element.style.transitionDuration = duration;
        element.style.pointerEvents = 'auto';
      });

      const distance = touchPosition.current.start - touchPosition.current.end;
      const stepWidth = elementWidth.current * stepSize + gap * (stepSize - 1);

      try {
        // Если не пройден заданный порог для смещения шага
        if (Math.abs(distance) / stepWidth < swipe) throw new Error();
        if (currentStep === 0 && distance < 0) throw new Error();
        if (currentStep === maxStep && distance > 0) throw new Error();

        const movedStepsWithGap = Math.abs(distance) / (stepWidth + gap);
        const remainder = Math.abs(distance) % (stepWidth + gap);

        const isRemainderStep = remainder < stepWidth;
        const isThresholdStep = remainder / stepWidth >= swipe;

        let movedSteps = (isRemainderStep ? Math.floor : Math.ceil)(movedStepsWithGap);

        if (isRemainderStep && isThresholdStep) {
          movedSteps += 1;
        }

        if (distance !== 0) {
          let nextStep = currentStep;

          nextStep = distance < 0 ? nextStep - movedSteps : nextStep;
          nextStep = distance > 0 ? nextStep + movedSteps : nextStep;
          nextStep = nextStep < 0 ? 0 : nextStep > maxStep ? maxStep : nextStep;

          setCurrentStep?.(nextStep);
          onChangeStep?.(nextStep);
        }

        elementTranslateX.current.prev = elementTranslateX.current.next;
      } catch (error) {
        elements.current.forEach((element) => {
          element.style.transform = getTranslateStyle(elementTranslateX.current, 'prev');
          element.style.transitionDuration = duration;
        });

        elementTranslateX.current.next = elementTranslateX.current.prev;
      }
    }
  };

  // `temporaryElements` временно заполняется элементами, чтобы объект `elements`
  // всегда содержал правильное количество элементов
  const temporaryElements = [];

  const setItemElements = (ref) => {
    if (ref !== null) {
      temporaryElements.push(ref);
      elements.current = temporaryElements;
    }
  };

  // `temporaryPlugs` временно заполняется элементами, чтобы объект `elements`
  // всегда содержал правильное количество элементов
  const temporaryPlugs = [];

  const setPlugElements = (ref) => {
    if (ref !== null) {
      temporaryPlugs.push(ref);
      plugs.current = temporaryPlugs;
    }
  };

  setElementTranslate();

  return (
    <div
      ref={container}
      role="presentation"
      style={{
        display: 'flex',
        height: 'inherit',
        minHeight: 'inherit',
        maxHeight: 'inherit',
        userSelect: 'none',
        gap,
        ...style,
      }}
      // Touch events
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      // Mouse events
      onMouseDown={handleTouchStart}
      onMouseMove={handleTouchMove}
      onMouseUp={handleTouchEnd}
      onMouseLeave={handleTouchEnd}
      {...props}
    >
      {Children.map(children, (child, index) => {
        if (!child) return null;

        return (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            ref={setItemElements}
            style={{
              position: 'relative',
              height: 'inherit',
              minHeight: 'inherit',
              maxHeight: 'inherit',
              width: getWidthStyle({ trackSize, gap }),
              minWidth: getWidthStyle({ trackSize, gap }),
              maxWidth: getWidthStyle({ trackSize, gap }),
              transform: getTranslateStyle(elementTranslateX.current, 'next'),
              transition: 'all',
              transitionDelay: delay,
              transitionDuration: duration,
              transitionTimingFunction: timing,
              pointerEvents: 'auto',
            }}
          >
            <div
              ref={setPlugElements}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                cursor: 'grabbing',
                zIndex: 999999999,
              }}
            />
            {child}
          </div>
        );
      })}
    </div>
  );
}
